/**
 * A7Y-1895: first-party affiliate visit tracking — the sole attribution path
 * (the legacy Rewardful `rw.js` loader was retired in the A7Y-1900 cutover).
 *
 * Flow:
 *  - `parseViaToken()` reads `?via=<token>` synchronously at boot (pre-consent).
 *  - `captureVia()` runs at boot: it stores the token in module memory and a
 *    same-tab sessionStorage handoff. In a non-consent-required region (or when
 *    marketing consent is already granted)
 *    it writes immediately; in a consent-required region the handoff survives
 *    navigation/reload until the visitor grants marketing consent. The visit
 *    POST, cookie, and durable localStorage attribution remain consent-gated.
 *  - `onMarketingConsentGranted()` POSTs to `/affiliate/track/visit`, then
 *    persists first-party attribution (apex cookie `a7y-aff` + localStorage
 *    keys). FIRST-TOUCH: an existing, unexpired referral is never overwritten.
 *  - `clearAffiliateState()` removes every trace (called on marketing-consent
 *    revocation).
 *
 * localStorage values are stored as RAW strings (not JSON) so the tiny
 * dependency-free blog/docs inline snippet — same-origin, sharing this
 * localStorage — can read/write the identical keys without a JSON codec.
 */

import { buildApiUrl } from '@a7y/api-client';
import { AFFILIATE_COOKIE_WINDOW_DAYS } from '@a7y/types';
import { cookieMustBeHostOnly } from '@a7y/types/deployment-environment';
import { AFFILIATE_VIA_PARAM, SS_PENDING_TOKEN } from './affiliate-handoff-contract';
import { hasMarketingConsent } from './consent';
import { mpRegister } from './mixpanel';

export { AFFILIATE_VIA_PARAM, SS_PENDING_TOKEN } from './affiliate-handoff-contract';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Apex cookie name. Mirrors the blog/docs inline snippet. */
export const AFF_COOKIE = 'a7y-aff';

/**
 * First-party visitor-id cookie set by the webapp worker (`buildVisitorIdCookie`
 * in `worker/index.ts`) on `Domain=.apptonomy.ai`. Shared across www/blog/docs.
 * Used as the affiliate `visitorId` so the backend can key the deterministic
 * referral doc id on a stable per-browser identity.
 */
export const VISITOR_ID_COOKIE = 'a7y-vid';

/** localStorage keys (raw-string values, shared with blog/docs). */
export const LS_REFERRAL_ID = 'a7y-aff-referral-id';
export const LS_TOKEN = 'a7y-aff-token';
export const LS_EXPIRES = 'a7y-aff-expires';

/**
 * A7Y-1895: idempotency guard for the authed lead-promotion backstop. Stores
 * the referral id that was last successfully promoted so the post-auth fallback
 * (`apiPromoteAffiliateLead`) fires at most once per referral per browser. The
 * server side is idempotent too, so this only prevents needless re-sends.
 */
export const LS_LEAD_SENT = 'a7y-aff-lead-sent';

/**
 * localStorage fallback visitor-id key. Used only when the `a7y-vid` cookie is
 * missing/malformed (e.g. local dev, preview deploys). Shared same-origin with
 * the blog/docs inline snippet so the fallback identity stays consistent.
 */
export const LS_VISITOR_ID = 'a7y-aff-vid';

/**
 * Backend contract: `visitorId` must match this exactly (see
 * `/affiliate/track/visit`). A `crypto.randomUUID()` value satisfies it
 * (hex + hyphens, 36 chars).
 */
const VISITOR_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

/** The attribution cookie lives for the shared fallback window (A7Y-5185). */
const COOKIE_TTL_SECONDS = AFFILIATE_COOKIE_WINDOW_DAYS * 24 * 60 * 60;

/** Visit-tracking endpoint, product-scoped under `/:productId/affiliate/...`. */
const VISIT_ENDPOINT = 'affiliate/track/visit';

// ---------------------------------------------------------------------------
// Pending token handoff (pre-consent)
// ---------------------------------------------------------------------------

/**
 * In-memory fast path for the `?via=` token captured at boot. sessionStorage is
 * the reload-safe handoff; this remains the fallback when storage is blocked.
 */
let pendingViaToken: string | null = null;

/** Test/reset seam — clears the in-memory pending token. */
export function __resetPendingViaToken(): void {
  pendingViaToken = null;
}

function storePendingViaToken(token: string): void {
  pendingViaToken = token;
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SS_PENDING_TOKEN, token);
  } catch {
    // sessionStorage unavailable — module memory still covers same-document consent.
    return;
  }
}

function readPendingViaToken(): string | null {
  if (pendingViaToken) return pendingViaToken;
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SS_PENDING_TOKEN);
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

function clearPendingViaToken(): void {
  pendingViaToken = null;
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SS_PENDING_TOKEN);
  } catch {
    // sessionStorage unavailable — module memory was still cleared above.
    return;
  }
}

interface TrackVisitResponse {
  referralId?: string | null;
  expiresAt?: string | null;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/** Reads `?via=<token>` from the current URL. Sync, safe pre-consent. */
export function parseViaToken(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const raw = new URLSearchParams(window.location.search).get(AFFILIATE_VIA_PARAM);
  if (!raw) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// ---------------------------------------------------------------------------
// Visitor id (stable per-browser identity for the affiliate visit POST)
// ---------------------------------------------------------------------------

/** Read a cookie value by name from `document.cookie`. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  for (const raw of document.cookie.split(';')) {
    const [k, ...rest] = raw.trim().split('=');
    if (k === name) return rest.join('=');
  }
  return null;
}

/**
 * Returns a stable per-browser visitor id for the affiliate visit POST.
 *
 * Prefers the first-party `a7y-vid` cookie the webapp worker sets on
 * `Domain=.apptonomy.ai` (shared across www/blog/docs). When that cookie is
 * absent or malformed (local dev, preview deploys), reads-or-creates a
 * persisted `crypto.randomUUID()` fallback in localStorage under
 * `a7y-aff-vid`. The returned value always matches the backend's
 * `/^[A-Za-z0-9_-]{8,64}$/` contract.
 */
export function getVisitorId(): string {
  const fromCookie = readCookie(VISITOR_ID_COOKIE);
  if (fromCookie && VISITOR_ID_RE.test(fromCookie)) return fromCookie;

  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const existing = localStorage.getItem(LS_VISITOR_ID);
      if (existing && VISITOR_ID_RE.test(existing)) return existing;
      const generated = crypto.randomUUID();
      localStorage.setItem(LS_VISITOR_ID, generated);
      return generated;
    } catch {
      // localStorage unavailable — fall through to an ephemeral id.
    }
  }
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Cookie helper (mirrors buildVisitorIdCookie, WITH the staging carve-out)
// ---------------------------------------------------------------------------

/**
 * Build the `a7y-aff` cookie string.
 *
 * Staging/Sandbox carve-out (opposite of `buildVisitorIdCookie` in
 * worker/index.ts), now keyed on the shared deployment-environment contract's
 * `cookieMustBeHostOnly` rather than a literal staging-only hostname check:
 *  - `www-staging.apptonomy.ai` / `www-sandbox.apptonomy.ai` → host-only, NO
 *    Domain attribute.
 *  - any other `*.apptonomy.ai` (production) → `Domain=.apptonomy.ai` (spans
 *    www/api).
 *  - everything else (localhost, *.workers.dev preview) → host-only.
 */
export function buildAffiliateCookie(host: string, value: string): string {
  const bareHost = host.split(':')[0];
  let domainAttr = '';
  if (cookieMustBeHostOnly(bareHost)) {
    domainAttr = ''; // host-only on staging/sandbox
  } else if (bareHost.endsWith('.apptonomy.ai')) {
    domainAttr = 'Domain=.apptonomy.ai';
  }
  return [
    `${AFF_COOKIE}=${value}`,
    'Path=/',
    `Max-Age=${COOKIE_TTL_SECONDS}`,
    'SameSite=Lax',
    'Secure',
    domainAttr,
  ]
    .filter(Boolean)
    .join('; ');
}

function deleteAffiliateCookie(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const bareHost = window.location.hostname.split(':')[0];
  const domains: Array<string | null> = [null, bareHost, '.' + bareHost];
  if (bareHost.endsWith('.apptonomy.ai') && !cookieMustBeHostOnly(bareHost)) {
    domains.push('.apptonomy.ai');
  }
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  for (const domain of domains) {
    const domainAttr = domain ? `;domain=${domain}` : '';
    document.cookie = `${AFF_COOKIE}=;${expired};path=/${domainAttr}`;
  }
}

// ---------------------------------------------------------------------------
// Storage read/write (raw-string values, shared with blog/docs snippet)
// ---------------------------------------------------------------------------

/** Read the stored referral id (used by `<SignUp unsafeMetadata>`). */
export function getStoredReferralId(): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    return localStorage.getItem(LS_REFERRAL_ID);
  } catch {
    return null;
  }
}

/**
 * FIRST-TOUCH guard: returns true when a stored referral exists and has not
 * expired. Such a referral must never be overwritten by a later `?via=`.
 */
function hasUnexpiredReferral(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  const existing = localStorage.getItem(LS_REFERRAL_ID);
  if (!existing) return false;
  const expires = localStorage.getItem(LS_EXPIRES);
  if (!expires) return true; // referral present, no expiry → treat as unexpired
  const ts = Date.parse(expires);
  if (Number.isNaN(ts)) return true;
  return ts > Date.now();
}

function writeAffiliateState(token: string, referralId: string, expiresAt: string | null): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const expires = expiresAt ?? new Date(Date.now() + COOKIE_TTL_SECONDS * 1000).toISOString();
  try {
    localStorage.setItem(LS_REFERRAL_ID, referralId);
    localStorage.setItem(LS_TOKEN, token);
    localStorage.setItem(LS_EXPIRES, expires);
  } catch {
    // localStorage unavailable — non-fatal
  }
  if (typeof document !== 'undefined') {
    document.cookie = buildAffiliateCookie(window.location.hostname, referralId);
  }
}

// ---------------------------------------------------------------------------
// Capture + persist
// ---------------------------------------------------------------------------

/**
 * Boot-time capture. Stores the `?via=` token in a same-tab handoff. Persists
 * affiliate attribution immediately only when marketing tracking is allowed
 * (`hasMarketingConsent()` — true in non-required regions AND when the
 * visitor has already granted consent).
 */
export function captureVia(): void {
  const token = parseViaToken();
  if (token) storePendingViaToken(token);
  if (hasMarketingConsent() && readPendingViaToken()) {
    void onMarketingConsentGranted();
  }
}

/**
 * Called when marketing consent is granted (or at boot for already-allowed
 * visitors). POSTs the visit, then persists first-party attribution.
 * FIRST-TOUCH: never overwrites an existing unexpired referral.
 */
export async function onMarketingConsentGranted(): Promise<void> {
  const token = readPendingViaToken() ?? parseViaToken();
  if (!token) return;
  if (hasUnexpiredReferral()) {
    clearPendingViaToken();
    return;
  }

  let referralId: string | null = null;
  let expiresAt: string | null = null;
  try {
    const response = await fetch(buildApiUrl(VISIT_ENDPOINT, null), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, visitorId: getVisitorId() }),
    });
    if (response.ok) {
      const data = (await response.json()) as TrackVisitResponse;
      referralId = data?.referralId ?? null;
      expiresAt = data?.expiresAt ?? null;
    }
  } catch {
    // Network / CORS failure — non-fatal, nothing persisted.
  }

  if (!referralId) return;

  writeAffiliateState(token, referralId, expiresAt);
  // Consent-gated inside mpRegister → mp(); safe to call unconditionally.
  mpRegister({ affiliate_token: token, referral_id: referralId });
  clearPendingViaToken();
}

/** Remove every trace of affiliate attribution (marketing-consent revoke). */
export function clearAffiliateState(): void {
  clearPendingViaToken();
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      localStorage.removeItem(LS_REFERRAL_ID);
      localStorage.removeItem(LS_TOKEN);
      localStorage.removeItem(LS_EXPIRES);
      localStorage.removeItem(LS_VISITOR_ID);
      localStorage.removeItem(LS_LEAD_SENT);
    } catch {
      // ignore
    }
  }
  deleteAffiliateCookie();
}
